import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  ChurchFilters,
  NearbyChurch,
  PublicChurch,
  filterChurches,
  nearestChurches,
  parseCoordinate,
  regionOptions,
} from './churchFinder';
import { buildInfoContent } from './churchInfoContent';

declare global {
  interface Window {
    // NAVER Maps v3 는 타입 패키지가 없어 any 로 둔다.
    naver?: any;
  }
}

/**
 * 도메인 제한이 걸린 공개 클라이언트 키다. Client Secret 이 아니므로 번들에 들어가도 된다.
 * NAVER Cloud 콘솔의 Web 서비스 URL 에 https://awanaevent.com 이 등록되어 있어야 동작한다.
 */
const NAVER_MAP_CLIENT_ID = 'sqgtmwwnz8';
const NAVER_SDK_SRC = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${NAVER_MAP_CLIENT_ID}`;

/** Google Sheet 를 공개하는 Apps Script Web App. 주소가 바뀌면 여기만 고치면 된다. */
const AWANA_DATA_URL =
  'https://script.google.com/macros/s/AKfycbxPkCV_TFtk0lfnLH_sMY6Bo6PNJYt_fI-e_ttmzLjgGd1aUz0J8Dw1rOnV7egjinLS/exec';

const KOREA_CENTER = { lat: 36.3, lng: 127.8 };
const KOREA_ZOOM = 7;
const NEARBY_LIMIT = 10;
const FIT_PADDING = { top: 60, right: 60, bottom: 60, left: 60 };

const CLUBS = [
  { value: 'PUGGLES', label: 'Puggles' },
  { value: 'CUBBIES', label: 'Cubbies' },
  { value: 'SPARKS', label: 'Sparks' },
  { value: 'T&T', label: 'T&T' },
  { value: 'TREK', label: 'Trek' },
  { value: 'JOURNEY', label: 'Journey' },
];

let sdkPromise: Promise<void> | null = null;

/** NAVER 지도 SDK 를 한 번만 불러온다. 지도를 안 보는 페이지에는 로드되지 않는다. */
function loadNaverSdk(): Promise<void> {
  if (window.naver && window.naver.maps) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = NAVER_SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error('NAVER 지도 SDK 로드 실패'));
    };
    document.head.appendChild(script);
  });

  return sdkPromise;
}

/**
 * 전달받은 원본 HTML 은 JSONP 로 받았지만, 배포된 Apps Script 는 `callback=` 을 무시하고
 * 순수 JSON 을 돌려준다. JSONP 로 받으면 script 태그가 JSON 을 JS 로 파싱하다 SyntaxError 를
 * 내는데 그건 onerror 를 발동시키지 않아서 로딩 상태에 영원히 갇힌다.
 * 응답에 Access-Control-Allow-Origin: * 가 붙어 있으므로 그냥 fetch 로 읽는다.
 */
async function fetchPublicChurches(): Promise<PublicChurch[]> {
  const response = await fetch(`${AWANA_DATA_URL}?data=1`);
  if (!response.ok) {
    throw new Error(`Apps Script 응답 오류: ${response.status}`);
  }

  const payload = (await response.json()) as { churches?: PublicChurch[] };
  return Array.isArray(payload && payload.churches) ? (payload.churches as PublicChurch[]) : [];
}

interface ChurchFinderMapProps {
  /** 지도 탭이 보이는 중인지. 숨겼다 다시 보일 때 지도 크기를 다시 잡아준다. */
  active?: boolean;
}

const ChurchFinderMap: React.FC<ChurchFinderMapProps> = ({ active = true }) => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const infoWindowRef = useRef<any>(null);
  const myLocationMarkerRef = useRef<any>(null);
  // 항상 같은 배열을 제자리에서 비우고 채운다(재할당 금지) — 정리 함수가 안전하게 붙잡을 수 있도록.
  const markersRef = useRef<any[]>([]);
  const shouldFitRef = useRef(false);

  const [mapReady, setMapReady] = useState(false);
  const [churches, setChurches] = useState<PublicChurch[]>([]);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [keyword, setKeyword] = useState('');
  const [region, setRegion] = useState('');
  const [club, setClub] = useState('');
  const [nearby, setNearby] = useState<NearbyChurch[] | null>(null);

  // 지도 생성 + 데이터 로드
  useEffect(() => {
    let cancelled = false;
    let mapInstance: any = null;
    const markers = markersRef.current;
    const globalScope = window as unknown as Record<string, unknown>;

    // 인증에 실패하면 NAVER 가 이 전역 함수를 부른다. 안 걸어두면 회색 화면만 남는다.
    globalScope.navermap_authFailure = () => {
      if (cancelled) return;
      setError(
        '네이버 지도 인증에 실패했습니다. NAVER Cloud 콘솔의 Web 서비스 URL 에 이 도메인이 등록되어 있는지 확인해 주세요.'
      );
    };

    loadNaverSdk()
      .then(() => {
        if (cancelled || !mapElementRef.current) return;
        const { naver } = window;
        if (!naver || !naver.maps) throw new Error('NAVER 지도 SDK 없음');

        mapInstance = new naver.maps.Map(mapElementRef.current, {
          center: new naver.maps.LatLng(KOREA_CENTER.lat, KOREA_CENTER.lng),
          zoom: KOREA_ZOOM,
          zoomControl: true,
          zoomControlOptions: { position: naver.maps.Position.TOP_RIGHT },
          mapTypeControl: true,
        });
        mapRef.current = mapInstance;
        infoWindowRef.current = new naver.maps.InfoWindow({
          borderWidth: 0,
          backgroundColor: '#ffffff',
        });

        // 지도 빈 곳을 누르면 팝업이 닫힌다. NAVER InfoWindow 는 이걸 알아서 해 주지 않는다.
        naver.maps.Event.addListener(mapInstance, 'click', () => {
          if (infoWindowRef.current) infoWindowRef.current.close();
        });

        setMapReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setError(
          '네이버 지도를 불러오지 못했습니다. NAVER Cloud 콘솔의 Web 서비스 URL 등록을 확인해 주세요.'
        );
      });

    fetchPublicChurches()
      .then((list) => {
        if (!cancelled) setChurches(list);
      })
      .catch(() => {
        if (!cancelled) {
          setError('교회 데이터를 불러오지 못했습니다. Apps Script 배포와 공개 권한을 확인해 주세요.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      delete globalScope.navermap_authFailure;
      markers.forEach((marker) => marker.setMap(null));
      markers.length = 0;
      if (mapInstance && typeof mapInstance.destroy === 'function') mapInstance.destroy();
    };
  }, []);

  const regions = useMemo(() => regionOptions(churches), [churches]);
  const filters: ChurchFilters = useMemo(() => ({ keyword, region, club }), [keyword, region, club]);
  const filtered = useMemo(() => filterChurches(churches, filters), [churches, filters]);
  const visible = nearby ?? filtered;

  // 좌표가 없는 교회는 지도에 못 찍는다. 상태 문구도 실제로 찍힌 개수를 센다.
  const plotted = useMemo(
    () =>
      visible.filter(
        (church) =>
          parseCoordinate(church.latitude) !== null && parseCoordinate(church.longitude) !== null
      ),
    [visible]
  );

  // 마커 다시 그리기
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const { naver } = window;
    if (!naver || !naver.maps) return;

    const map = mapRef.current;
    const markers = markersRef.current;

    markers.forEach((marker) => marker.setMap(null));
    markers.length = 0;
    if (infoWindowRef.current) infoWindowRef.current.close();

    const bounds = new naver.maps.LatLngBounds();

    plotted.forEach((church) => {
      const lat = parseCoordinate(church.latitude) as number;
      const lng = parseCoordinate(church.longitude) as number;
      const position = new naver.maps.LatLng(lat, lng);
      const marker = new naver.maps.Marker({ position, map, title: church.name || '' });

      naver.maps.Event.addListener(marker, 'click', () => {
        if (!infoWindowRef.current) return;
        infoWindowRef.current.setContent(
          buildInfoContent(church, () => {
            if (infoWindowRef.current) infoWindowRef.current.close();
          })
        );
        infoWindowRef.current.open(map, marker);
      });

      markers.push(marker);
      bounds.extend(position);
    });

    if (shouldFitRef.current && plotted.length > 0) {
      if (plotted.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(15);
      } else {
        map.fitBounds(bounds, FIT_PADDING);
      }
    }
    shouldFitRef.current = false;
  }, [mapReady, plotted]);

  // display:none 인 동안에는 지도가 자기 크기를 모른다. 다시 보일 때 한 번 알려준다.
  useEffect(() => {
    if (!active || !mapReady || !mapRef.current) return;
    const { naver } = window;
    if (naver && naver.maps) naver.maps.Event.trigger(mapRef.current, 'resize');
  }, [active, mapReady]);

  /** 검색/필터를 건드리면 '내 주변' 결과는 풀고, 결과에 맞춰 화면을 맞춘다. */
  const beginFilterChange = () => {
    shouldFitRef.current = true;
    setError(null);
    setNearby(null);
  };

  const handleReset = () => {
    shouldFitRef.current = false;
    setKeyword('');
    setRegion('');
    setClub('');
    setNearby(null);
    setError(null);

    if (myLocationMarkerRef.current) {
      myLocationMarkerRef.current.setMap(null);
      myLocationMarkerRef.current = null;
    }

    const { naver } = window;
    if (mapRef.current && naver && naver.maps) {
      mapRef.current.setCenter(new naver.maps.LatLng(KOREA_CENTER.lat, KOREA_CENTER.lng));
      mapRef.current.setZoom(KOREA_ZOOM);
    }
  };

  const handleFindNearby = () => {
    if (!navigator.geolocation) {
      setError('현재 위치 기능을 지원하지 않는 브라우저입니다.');
      return;
    }

    setError(null);
    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        const { latitude: myLat, longitude: myLng } = position.coords;

        const { naver } = window;
        if (mapRef.current && naver && naver.maps) {
          if (myLocationMarkerRef.current) myLocationMarkerRef.current.setMap(null);
          myLocationMarkerRef.current = new naver.maps.Marker({
            position: new naver.maps.LatLng(myLat, myLng),
            map: mapRef.current,
            title: '현재 위치',
          });
        }

        shouldFitRef.current = true;
        setNearby(nearestChurches(churches, myLat, myLng, NEARBY_LIMIT));
      },
      () => {
        setLocating(false);
        setError('현재 위치를 확인할 수 없습니다. 브라우저에서 위치 사용을 허용해 주세요.');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  const status = useMemo(() => {
    if (loading) return '등록교회 데이터를 불러오는 중입니다...';
    if (locating) return '현재 위치를 확인하고 있습니다...';
    if (churches.length === 0) return '표시할 등록교회가 없습니다.';
    if (nearby) return `현재 위치에서 가까운 등록교회 ${plotted.length}개를 표시합니다.`;
    if (plotted.length === churches.length) {
      return `등록교회 ${churches.length}개를 표시하고 있습니다.`;
    }
    return `등록교회 ${churches.length}개 중 ${plotted.length}개를 표시하고 있습니다.`;
  }, [loading, locating, churches.length, nearby, plotted.length]);

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
        Awana 등록교회 찾기
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        가까운 Awana 등록교회와 운영 중인 클럽을 확인하세요.
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ mt: 2.5, flexWrap: 'wrap' }}
      >
        <TextField
          label="교회명 또는 주소 검색"
          type="search"
          size="small"
          value={keyword}
          onChange={(e) => {
            beginFilterChange();
            setKeyword(e.target.value);
          }}
          sx={{ flex: { md: '1 1 320px' }, minWidth: { md: 240 } }}
        />

        <TextField
          select
          label="지역"
          size="small"
          value={region}
          onChange={(e) => {
            beginFilterChange();
            setRegion(e.target.value);
          }}
          sx={{ minWidth: { md: 140 } }}
        >
          <MenuItem value="">전국</MenuItem>
          {regions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="클럽"
          size="small"
          value={club}
          onChange={(e) => {
            beginFilterChange();
            setClub(e.target.value);
          }}
          sx={{ minWidth: { md: 150 } }}
        >
          <MenuItem value="">전체 클럽</MenuItem>
          {CLUBS.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          startIcon={<MyLocationIcon />}
          onClick={handleFindNearby}
          disabled={loading || locating || churches.length === 0}
        >
          내 주변 교회
        </Button>

        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>
          전체보기
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        {status}
      </Typography>

      <Box
        ref={mapElementRef}
        sx={{
          width: '100%',
          height: { xs: 620, md: 680 },
          minHeight: 520,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.100',
        }}
      />
    </Box>
  );
};

export default ChurchFinderMap;
